import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaBriefcase,
  FaPlaneDeparture,
  FaGraduationCap,
  FaBuilding,
  FaFileSignature,
  FaIdCard,
  FaArrowRight,
  FaCheckCircle,
  FaWhatsapp,
  FaPhoneAlt,
  FaFileAlt,
  FaHandshake,
  FaSearch,
} from "react-icons/fa";
import CountUp from "react-countup";

const SERVICES = [
  {
    slug: "work-visa",
    Icon: FaBriefcase,
    title: "Work Visa Assistance",
    tagline: "Employment & Skilled Worker Visas",
    desc: "Professional guidance for employment visas and work permits across multiple countries. We navigate complex immigration laws so you can focus on your career.",
    highlights: ["Eligibility Assessment", "Employer Sponsorship", "Document Preparation", "Application Tracking"],
    processingTime: "3–12 weeks",
    countries: ["Canada", "UK", "Germany", "Australia"],
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&auto=format&fit=crop",
    color: "blue",
  },
  {
    slug: "travel-visa",
    Icon: FaPlaneDeparture,
    title: "Tourist & Travel Visas",
    tagline: "Short-Stay & Visit Visas",
    desc: "Fast and reliable tourist visa processing for individuals and families. We handle all documentation so your trip planning stays stress-free.",
    highlights: ["Tourist Visa Consultation", "Itinerary Help", "Document Review", "Embassy Liaison"],
    processingTime: "1–4 weeks",
    countries: ["USA", "UK", "Schengen", "UAE"],
    image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=600&auto=format&fit=crop",
    color: "sky",
  },
  {
    slug: "student-visa",
    Icon: FaGraduationCap,
    title: "Student Visa Services",
    tagline: "Study Abroad Visa Support",
    desc: "Supporting students from university selection through to visa approval. We make your international education dream a reality.",
    highlights: ["University Selection", "Eligibility Assessment", "Admission Documents", "Interview Prep"],
    processingTime: "4–10 weeks",
    countries: ["Canada", "UK", "Australia", "Germany"],
    image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&auto=format&fit=crop",
    color: "green",
  },
  {
    slug: "business-visa",
    Icon: FaBuilding,
    title: "Business Visa Support",
    tagline: "Investor & Entrepreneur Visas",
    desc: "Immigration support for entrepreneurs and professionals seeking global business opportunities, investor programs and corporate relocation.",
    highlights: ["Business Visa Consultation", "Investor Visa", "Corporate Documentation", "Application Submission"],
    processingTime: "3–8 weeks",
    countries: ["UAE", "UK", "Singapore", "Canada"],
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&auto=format&fit=crop",
    color: "purple",
  },
  {
    slug: "attestation",
    Icon: FaFileSignature,
    title: "Certificate Attestation",
    tagline: "Document Verification & Legalization",
    desc: "Reliable attestation services for educational, personal and commercial documents. We ensure your documents are legally recognised abroad.",
    highlights: ["Educational Attestation", "Personal Documents", "Commercial Attestation", "Embassy Legalization"],
    processingTime: "1–3 weeks",
    countries: ["UAE", "GCC Countries", "Worldwide"],
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&auto=format&fit=crop",
    color: "orange",
  },
  {
    slug: "trade-license",
    Icon: FaIdCard,
    title: "Trade License Services",
    tagline: "UAE Company Formation & Business Setup",
    desc: "Full-service company formation and trade license processing in the UAE. We handle approvals, government registration and all documentation.",
    highlights: ["Company Formation", "Trade License Registration", "Document Preparation", "Government Approvals"],
    processingTime: "1–4 weeks",
    countries: ["Dubai", "Abu Dhabi", "Sharjah", "UAE Freezone"],
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&auto=format&fit=crop",
    color: "red",
  },
];

const COLOR_MAP = {
  blue: { bg: "bg-blue-50", icon: "text-blue-600", border: "border-blue-100", badge: "bg-blue-100 text-blue-700" },
  sky: { bg: "bg-sky-50", icon: "text-sky-600", border: "border-sky-100", badge: "bg-sky-100 text-sky-700" },
  green: { bg: "bg-green-50", icon: "text-green-600", border: "border-green-100", badge: "bg-green-100 text-green-700" },
  purple: { bg: "bg-purple-50", icon: "text-purple-600", border: "border-purple-100", badge: "bg-purple-100 text-purple-700" },
  orange: { bg: "bg-orange-50", icon: "text-orange-500", border: "border-orange-100", badge: "bg-orange-100 text-orange-700" },
  red: { bg: "bg-red-50", icon: "text-red-500", border: "border-red-100", badge: "bg-red-100 text-red-700" },
};

export default function Services() {
  const [search, setSearch] = useState("");

  const filtered = SERVICES.filter(
    (s) =>
      s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.tagline.toLowerCase().includes(search.toLowerCase()) ||
      s.desc.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <Helmet>
        <title>Visa & Immigration Services | Lumis Documents Clearing Dubai</title>
        <meta name="description" content="Professional visa and immigration services including Work Visas, Student Visas, Tourist Visas, Business setups and Trade Licenses in the UAE." />
      </Helmet>
      {/* HERO */}
      <section
        className="h-[420px] flex items-center justify-center text-white bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=2070&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center px-6">
          <p className="tracking-widest text-blue-400 text-sm mb-3 uppercase">
            Professional Immigration Support
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>

          <p className="text-gray-200 max-w-xl mx-auto mb-5">
            Expert visa assistance, document clearing and immigration consulting
            for individuals, students and businesses worldwide.
          </p>

          <div className="text-gray-300 text-sm">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white font-medium">Services</span>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { end: 6, suffix: "+", label: "Services Offered" },
            { end: 1200, suffix: "+", label: "Visas Processed" },
            { end: 98, suffix: "%", label: "Success Rate" },
            { end: 16, suffix: "+", label: "Countries Covered" },
          ].map(({ end, suffix, label }) => (
            <div key={label}>
              <h3 className="text-3xl font-bold">
                <CountUp end={end} duration={2.5} enableScrollSpy scrollSpyDelay={200} />
                {suffix}
              </h3>
              <p className="text-blue-100 text-sm mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="bg-gray-100 py-10">
        <div className="max-w-2xl mx-auto px-6">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search services (e.g. work visa, student, attestation)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm"
            />
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          {search && (
            <p className="text-gray-500 text-sm mb-8 text-center">
              Showing{" "}
              <span className="font-semibold text-gray-800">{filtered.length}</span>{" "}
              service{filtered.length !== 1 ? "s" : ""} for "
              <span className="text-blue-600">{search}</span>"
            </p>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <FaSearch className="text-5xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No services match your search.</p>
              <button
                onClick={() => setSearch("")}
                className="mt-4 text-blue-600 font-semibold hover:underline"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((service) => {
                const c = COLOR_MAP[service.color];
                return (
                  <div
                    key={service.slug}
                    className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl hover:-translate-y-2 transition duration-300 overflow-hidden border ${c.border} flex flex-col`}
                  >
                    {/* IMAGE */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                      {/* ICON BADGE */}
                      <div className={`absolute top-4 right-4 w-11 h-11 ${c.bg} rounded-xl flex items-center justify-center shadow`}>
                        <service.Icon className={`${c.icon} text-xl`} />
                      </div>

                      {/* PROCESSING TIME */}
                      <span className="absolute bottom-3 left-4 text-white text-xs font-medium bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full">
                        ⏱ {service.processingTime}
                      </span>
                    </div>

                    <div className="p-7 flex flex-col flex-1">
                      {/* TAGLINE */}
                      <p className={`text-xs font-semibold uppercase tracking-wider ${c.icon} mb-2`}>
                        {service.tagline}
                      </p>

                      {/* TITLE */}
                      <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition">
                        {service.title}
                      </h3>

                      {/* DESCRIPTION */}
                      <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                        {service.desc}
                      </p>

                      {/* HIGHLIGHTS */}
                      <div className="flex flex-wrap gap-2 mb-5">
                        {service.highlights.map((h) => (
                          <span
                            key={h}
                            className={`text-xs px-3 py-1 rounded-full font-medium ${c.badge}`}
                          >
                            {h}
                          </span>
                        ))}
                      </div>

                      {/* COUNTRIES */}
                      <p className="text-xs text-gray-400 mb-5">
                        🌍 {service.countries.join(" · ")}
                      </p>

                      {/* CTA */}
                      <Link
                        to={`/services/${service.slug}`}
                        className="mt-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition shadow hover:shadow-lg"
                      >
                        Learn More <FaArrowRight className="text-sm" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* WHY CHOOSE LUMIS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Why Choose Lumis
          </h2>
          <p className="text-center text-gray-500 mb-14">
            What sets our immigration services apart from the rest
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                Icon: FaCheckCircle,
                title: "98% Success Rate",
                desc: "Our track record speaks for itself — over 1,200 successful visa approvals with a near-perfect success rate.",
              },
              {
                Icon: FaFileAlt,
                title: "Expert Documentation",
                desc: "We verify, prepare and review every single document so your application is complete and error-free from day one.",
              },
              {
                Icon: FaHandshake,
                title: "Dedicated Consultant",
                desc: "Every client is assigned a personal consultant who guides them through the entire process from start to approval.",
              },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="group text-center p-10 bg-white border border-gray-100 rounded-xl shadow hover:shadow-2xl hover:-translate-y-2 transition duration-300"
              >
                <div className="w-16 h-16 bg-blue-50 group-hover:bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-5 transition duration-300">
                  <Icon className="text-blue-600 group-hover:text-white text-2xl transition duration-300" />
                </div>
                <h3 className="font-bold text-lg mb-3 group-hover:text-blue-600 transition">
                  {title}
                </h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">How It Works</h2>
          <p className="text-center text-gray-500 mb-14">
            A clear, transparent process from enquiry to visa approval
          </p>

          <div className="grid md:grid-cols-4 gap-8 text-center relative">
            {/* CONNECTING LINE */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-100 z-0" />

            {[
              { step: "01", title: "Free Consultation", desc: "Book a consultation to assess your eligibility and best visa pathway." },
              { step: "02", title: "Document Collection", desc: "We provide a precise checklist and verify every document you submit." },
              { step: "03", title: "Application Filing", desc: "We file your application to the authority and manage all follow-ups." },
              { step: "04", title: "Visa Approval", desc: "Receive your approval and get ready to start your international journey!" },
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative z-10">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5 shadow-lg">
                  {step}
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-blue-600 text-white py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Start Your Visa or Business Process Today
        </h2>
        <p className="mb-8 text-blue-100 max-w-xl mx-auto">
          Contact Lumis Documents Clearing for expert consultation and
          professional immigration services tailored to your destination.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-md"
          >
            Get Free Consultation
          </Link>
          <a
            href="https://wa.me/971547662665"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition flex items-center gap-2"
          >
            <FaWhatsapp /> WhatsApp Us
          </a>
        </div>
      </section>

      {/* FLOATING CONTACT WIDGET */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">
            Chat with us on WhatsApp
          </p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>
    </div>
  );
}
